// src/admin/components/OrganizationUsersDialog.tsx
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Organization } from '@/types/organization';
import { User } from '@/types/user';
import { apiFetch } from '@/util/apiClient';
import { PersonAdd } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { OrganizationUsersDepartmentsTable } from './OrganizationUsersDepartmentsTable';

interface OrganizationUsersDialogProps {
  organization: Organization | null;
  onClose: () => void;
}

export function OrganizationUsersDialog({
  organization,
  onClose,
}: OrganizationUsersDialogProps) {
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [removeUser, setRemoveUser] = useState<User | null>(null);

  // Search for users to add
  const { data: searchResults } = useQuery({
    queryKey: ['search-users', searchQuery],
    queryFn: async () => {
      if (!searchQuery) return [];
      return apiFetch<User[]>(
        `/api/search/users/?q=${encodeURIComponent(searchQuery)}`,
      );
    },
    enabled: !!searchQuery,
  });

  // Add user mutation
  const addUserMutation = useMutation({
    mutationFn: async (userUuid: string) => {
      if (!organization) return;
      await apiFetch(`/api/organizations/${organization.id}/users/`, {
        method: 'POST',
        body: JSON.stringify({ userUuid }),
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['organization-users', organization?.id],
      });
      setSearchQuery('');
    },
  });

  // Remove user mutation
  const removeUserMutation = useMutation({
    mutationFn: async (userUuid: string) => {
      if (!organization) return;
      await apiFetch(
        `/api/organizations/${organization.id}/users/${userUuid}/`,
        {
          method: 'DELETE',
        },
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['organization-users', organization?.id],
      });
      setRemoveUser(null);
    },
  });

  if (!organization) return null;

  return (
    <Dialog open={!!organization} onClose={onClose} maxWidth={'md'} fullWidth>
      <DialogTitle>Manage Organization Users</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 2 }}>
          {/* Search and add users */}
          <Paper sx={{ p: 2 }}>
            <Typography variant={'subtitle1'} gutterBottom>
              Add Users
            </Typography>
            <Stack spacing={2}>
              <TextField
                label={'Search users'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                fullWidth
                size={'small'}
              />
              {searchResults && searchResults.length > 0 && (
                <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                  {searchResults.map((user) => (
                    <Button
                      key={user.uuid}
                      variant={'outlined'}
                      size={'small'}
                      startIcon={<PersonAdd />}
                      onClick={() => void addUserMutation.mutate(user.uuid)}
                      fullWidth
                      sx={{ mb: 1, justifyContent: 'flex-start' }}
                    >
                      {`${user.firstName} ${user.lastName} (${user.email})`}
                    </Button>
                  ))}
                </Box>
              )}
            </Stack>
          </Paper>

          {organization && (
            <OrganizationUsersDepartmentsTable organization={organization} />
          )}
        </Stack>
      </DialogContent>

      {/* Confirm Remove User Dialog */}
      <ConfirmDialog
        open={!!removeUser}
        title={'Remove User'}
        message={`Are you sure you want to remove ${removeUser?.firstName} ${removeUser?.lastName} from this organization?`}
        onConfirm={() => {
          if (removeUser) {
            void removeUserMutation.mutate(removeUser.uuid);
          }
        }}
        onCancel={() => setRemoveUser(null)}
        confirmText={'Remove'}
        confirmColor={'error'}
      />
    </Dialog>
  );
}
